/**
 * Agent rate card — the TXR-1501 terms THIS agent's agreements are written on
 * (V061). The brokerage's numbers are the DEFAULTS: a blank field here inherits
 * them (shown as the placeholder), a filled one overrides them, and clearing a
 * field goes back to inheriting.
 *
 * Two faces of the same editor:
 *   - `MyRateCard` — the /rate-card page an AGENT edits their own card on.
 *   - `RateCardEditor userId=…` — embedded in the Staff "Manage" panel so a
 *     brokerage_admin / super_admin edits any agent's (the roster holds users.id;
 *     the server translates to the agents row).
 */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  agentDisclosureService,
  agentRateCardService,
  complianceDocsService,
  uploadComplianceDocFile,
  type AgentDisclosureUpdate,
  type AgentRateCard,
  type AgentRateCardUpdate,
  type BrokerageTermDefaults,
} from "@re-platform/api-client";
import { Button, Card, Toast } from "@re-platform/ui";

import { api } from "../../lib/api";

type TermKey = keyof AgentRateCardUpdate;

const FIELDS: {
  key: TermKey;
  label: string;
  hint: string;
  numeric?: "int" | "decimal";
  width?: number;
}[] = [
  {
    key: "market_area",
    label: "Market area (¶3C)",
    hint: "Should not be left blank.",
    width: 260,
  },
  {
    key: "county",
    label: "County (¶7H)",
    hint: "Blank blocks a listing agreement — ¶5.G names it as the place of payment.",
    width: 140,
  },
  {
    key: "purchase_fee_pct",
    label: "Purchase fee (¶7A1)",
    hint: "% of the sales price.",
    numeric: "decimal",
    width: 90,
  },
  {
    key: "lease_fee_pct",
    label: "Lease fee (¶7A2)",
    hint: "% of ONE MONTH'S rent — 100 = one month.",
    numeric: "decimal",
    width: 90,
  },
  {
    key: "protection_period_days",
    label: "Protection period (¶7F)",
    hint: "Days.",
    numeric: "int",
    width: 90,
  },
  {
    key: "representation_term_months",
    label: "Term length (¶4)",
    hint: "Months from the day it is sent.",
    numeric: "int",
    width: 90,
  },
];

/** The card's overrides as input strings ("" = inherit the default). */
function toDraft(card: AgentRateCard): Record<TermKey, string> {
  const draft = {} as Record<TermKey, string>;
  for (const f of FIELDS) {
    const v = card[f.key];
    draft[f.key] = v === null || v === undefined ? "" : String(v);
  }
  return draft;
}

/** Every field is named on save: a blank CLEARS the override (explicit null). */
function toUpdate(draft: Record<TermKey, string>): AgentRateCardUpdate {
  const body: AgentRateCardUpdate = {};
  for (const f of FIELDS) {
    const raw = draft[f.key].trim();
    if (raw === "") {
      body[f.key] = null;
    } else if (f.numeric === "int") {
      body[f.key] = Number(raw) as never;
    } else {
      body[f.key] = raw as never;
    }
  }
  return body;
}

export function RateCardEditor({ userId }: { userId?: string }) {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<TermKey, string> | null>(null);
  const target = userId ? { userId } : undefined;
  const queryKey = ["agent-rate-card", userId ?? "own"];

  const q = useQuery({
    queryKey,
    queryFn: () => agentRateCardService.get(api, target),
  });

  useEffect(() => {
    if (q.data) setDraft(toDraft(q.data));
  }, [q.data]);

  const save = useMutation({
    mutationFn: (body: AgentRateCardUpdate) =>
      agentRateCardService.update(api, body, target),
    onSuccess: (saved) => {
      setDraft(toDraft(saved));
      void queryClient.invalidateQueries({ queryKey });
      setToast("Saved — the next agreement sent carries these terms.");
    },
    onError: (err: Error) => setToast(err.message),
  });

  if (q.isError) {
    return (
      <p style={{ color: "var(--rp-color-danger)", fontSize: 13 }}>
        Couldn't load the rate card: {(q.error as Error).message}
      </p>
    );
  }

  const defaults: BrokerageTermDefaults | null =
    q.data?.brokerage_defaults ?? null;

  return (
    <div>
      <p
        style={{
          color: "var(--rp-color-text-muted)",
          fontSize: 13,
          marginTop: 0,
        }}
      >
        A blank field inherits the brokerage default (shown greyed). The
        paragraph numbers match the form the client signs.
      </p>
      <div style={grid}>
        {FIELDS.map((f) => {
          const fallback = defaults?.[f.key];
          return (
            <label
              key={f.key}
              style={{ display: "grid", gap: 4, fontSize: 12 }}
            >
              <span style={{ color: "var(--rp-color-text-muted)" }}>
                {f.label}
              </span>
              <input
                style={{ ...input, width: f.width ?? 180 }}
                inputMode={f.numeric ? "decimal" : undefined}
                placeholder={
                  fallback === null || fallback === undefined
                    ? ""
                    : String(fallback)
                }
                value={draft?.[f.key] ?? ""}
                onChange={(e) =>
                  setDraft((d) => (d ? { ...d, [f.key]: e.target.value } : d))
                }
              />
              <span
                style={{ color: "var(--rp-color-text-muted)", fontSize: 11 }}
              >
                {f.hint}
              </span>
            </label>
          );
        })}
      </div>
      <div style={{ marginTop: 16 }}>
        <Button
          onClick={() => draft && save.mutate(toUpdate(draft))}
          disabled={save.isPending || q.isLoading || !draft}
        >
          {save.isPending ? "Saving…" : "Save rate card"}
        </Button>
      </div>
      <Toast message={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

/**
 * The dual-capacity disclosure's two questions (V085, MLD Form 505).
 *
 * The form rides a purchase-with-loan packet whenever ONE licence holder is both
 * the real estate sales agent and the mortgage loan originator. Nothing we hold
 * can answer whether the real estate company has a business relationship with
 * the mortgage company the loan is referred to — only the agent knows — so it is
 * asked once here and every disclosure they send carries it.
 *
 * Shown for BOTH brokerage types, unlike the rate card: the agent this concerns
 * is precisely the one who sits on both sides.
 *
 * The two options are quoted almost verbatim from the form, on purpose. This is
 * a statutory disclosure the borrower reads, and an agent choosing between
 * paraphrases cannot tell which box they are actually ticking.
 */
export function DualCapacityEditor({ userId }: { userId?: string }) {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [draft, setDraft] = useState<AgentDisclosureUpdate | null>(null);
  const target = userId ? { userId } : undefined;
  const queryKey = ["agent-disclosure", userId ?? "own"];

  const q = useQuery({
    queryKey,
    queryFn: () => agentDisclosureService.get(api, target),
  });

  useEffect(() => {
    if (q.data)
      setDraft({
        mortgage_re_relationship: q.data.mortgage_re_relationship,
        mortgage_re_relationship_detail:
          q.data.mortgage_re_relationship_detail ?? "",
        mortgage_company_name: q.data.mortgage_company_name ?? "",
      });
  }, [q.data]);

  const save = useMutation({
    mutationFn: (body: AgentDisclosureUpdate) =>
      agentDisclosureService.update(api, body, target),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      setToast("Saved — the next dual-capacity disclosure carries this.");
    },
    onError: (err: Error) => setToast(err.message),
  });

  if (q.isError) {
    return (
      <p style={{ color: "var(--rp-color-danger)", fontSize: 13 }}>
        Couldn't load the disclosure: {(q.error as Error).message}
      </p>
    );
  }

  const claiming = draft?.mortgage_re_relationship === "HAS";
  const detail = (draft?.mortgage_re_relationship_detail ?? "").trim();
  // Mirrors the server's rule (and the DB CHECK): a ticked box with nothing
  // beside it discloses nothing. Blocking Save is kinder than a 422.
  const blocked = claiming && detail === "";
  // Every agent starts on the no-relationship default, which is the honest
  // answer for most of them — but a default is not a disclosure. Until someone
  // reads the question and confirms it, this form is printing a blank as though
  // it were an answer, and the one agent it is wrong about will never be asked.
  const unanswered =
    q.data != null && q.data.mortgage_re_relationship_set_at === null;

  return (
    <div>
      <p
        style={{
          color: "var(--rp-color-text-muted)",
          fontSize: 13,
          marginTop: 0,
        }}
      >
        Asked on the dual capacity disclosure your borrowers sign when you act
        as both their real estate agent and their loan originator.
      </p>
      {unanswered && (
        <p
          style={{
            color: "var(--rp-color-warning)",
            fontSize: 13,
            background: "var(--rp-color-warning-faint)",
            padding: "8px 10px",
            borderRadius: "var(--rp-radius-md)",
          }}
        >
          Not answered yet — your disclosures currently go out saying you have
          no business relationship with the mortgage company. Confirm that
          below, or say otherwise.
        </p>
      )}

      <label style={{ display: "grid", gap: 4, fontSize: 12, maxWidth: 460 }}>
        <span style={{ color: "var(--rp-color-text-muted)" }}>
          Mortgage company — name, MLD licence number and NMLS ID
        </span>
        <input
          style={{ ...input, width: "100%" }}
          placeholder="Purelend Mortgage, MLD #4471, NMLS #1902233"
          value={draft?.mortgage_company_name ?? ""}
          onChange={(e) =>
            setDraft((d) =>
              d ? { ...d, mortgage_company_name: e.target.value } : d,
            )
          }
        />
        <span style={{ color: "var(--rp-color-text-muted)", fontSize: 11 }}>
          The form asks for all three in one blank. This is the company's, not
          your own NMLS ID.
        </span>
      </label>

      <fieldset
        style={{
          border: "1px solid var(--rp-color-border)",
          borderRadius: "var(--rp-radius-md)",
          padding: "10px 12px",
          marginTop: 14,
          maxWidth: 640,
        }}
      >
        <legend style={{ fontSize: 12, color: "var(--rp-color-text-muted)" }}>
          Your real estate company…
        </legend>
        {(
          [
            [
              "NONE",
              "Does not have a business relationship with the licensed mortgage company or exempt company to which your loan is referred.",
            ],
            [
              "HAS",
              "Does have a business relationship with the licensed mortgage company or exempt company to which your loan is referred.",
            ],
          ] as const
        ).map(([value, label]) => (
          <label
            key={value}
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              fontSize: 13,
              padding: "5px 0",
            }}
          >
            <input
              type="radio"
              name={`dual-capacity-${userId ?? "own"}`}
              checked={draft?.mortgage_re_relationship === value}
              onChange={() =>
                setDraft((d) =>
                  d ? { ...d, mortgage_re_relationship: value } : d,
                )
              }
              style={{ marginTop: 3 }}
            />
            <span>{label}</span>
          </label>
        ))}
      </fieldset>

      {claiming && (
        <label
          style={{
            display: "grid",
            gap: 4,
            fontSize: 12,
            marginTop: 12,
            maxWidth: 640,
          }}
        >
          <span style={{ color: "var(--rp-color-text-muted)" }}>
            Describe the nature of the relationship, including percentage of
            ownership interest, if applicable
          </span>
          <textarea
            style={{
              ...input,
              width: "100%",
              minHeight: 72,
              resize: "vertical",
            }}
            maxLength={368}
            value={draft?.mortgage_re_relationship_detail ?? ""}
            onChange={(e) =>
              setDraft((d) =>
                d
                  ? { ...d, mortgage_re_relationship_detail: e.target.value }
                  : d,
              )
            }
          />
          <span style={{ color: "var(--rp-color-text-muted)", fontSize: 11 }}>
            Prints across the form's four lines — about 368 characters fit.
            {detail === "" && " Required when you have a relationship."}
          </span>
        </label>
      )}

      <div style={{ marginTop: 16 }}>
        <Button
          onClick={() => draft && save.mutate(draft)}
          disabled={save.isPending || q.isLoading || !draft || blocked}
        >
          {save.isPending ? "Saving…" : "Save disclosure"}
        </Button>
      </div>
      <Toast message={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

/**
 * The agent's own IABS (TREC's Information About Brokerage Services) —
 * per-licensee, so every agent uploads their own alongside the brokerage's
 * (PR B, V062). SOFT: missing one never blocks anything here, just nags —
 * an invite-accept flow that dead-ends on a missing PDF loses the agent.
 */
function ComplianceSection() {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryKey = ["compliance-docs", "own"];

  const q = useQuery({
    queryKey,
    queryFn: () => complianceDocsService.list(api),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const presigned = await complianceDocsService.presignUpload(api, "IABS");
      await uploadComplianceDocFile(presigned.upload_url, file);
      return complianceDocsService.confirmUpload(api, presigned.s3_key, "IABS");
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      setToast("IABS uploaded.");
    },
    onError: (err: Error) => setToast(err.message),
  });

  const hasIabs = q.data?.some((d) => d.kind === "IABS") ?? false;

  return (
    <Card padding="md" style={{ marginTop: 16 }}>
      <h2 style={{ fontSize: 14, marginTop: 0 }}>Compliance</h2>
      {!q.isLoading && !hasIabs && (
        <p
          style={{
            color: "var(--rp-color-danger)",
            fontSize: 13,
            background: "var(--rp-color-danger-faint)",
            padding: "8px 10px",
            borderRadius: "var(--rp-radius-md)",
          }}
        >
          Compliance incomplete — upload your Information About Brokerage
          Services (IABS) so clients can see it on your public page.
        </p>
      )}
      {hasIabs && (
        <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13 }}>
          Your IABS is on file.
        </p>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload.mutate(file);
          e.target.value = "";
        }}
      />
      <Button
        onClick={() => fileInputRef.current?.click()}
        disabled={upload.isPending}
      >
        {upload.isPending
          ? "Uploading…"
          : hasIabs
            ? "Replace IABS"
            : "Upload IABS"}
      </Button>
      <Toast message={toast} onDismiss={() => setToast(null)} />
    </Card>
  );
}

/** /rate-card — the agent's own card. Photo now lives on /profile (V068 —
 * works for every role, not just REAL_ESTATE agents). The TXR-1501 terms
 * below are a Texas real estate form; a MORTGAGE agent has no representation
 * agreement for them to apply to, so the form is swapped for a note instead
 * (Checkpoint #16). */
export function MyRateCard() {
  const q = useQuery({
    queryKey: ["agent-rate-card", "own"],
    queryFn: () => agentRateCardService.get(api),
  });

  return (
    <>
      <h1 style={{ marginTop: 0 }}>My rate card</h1>
      <p style={{ color: "var(--rp-color-text-muted)", marginTop: 0 }}>
        These print on every representation agreement sent for your clients.
      </p>
      {q.data?.brokerage_type === "MORTGAGE" ? (
        <Card padding="md" style={{ marginTop: 16 }}>
          <p style={{ margin: 0 }}>
            The TXR-1501 representation terms are a Texas real estate form —
            your brokerage doesn't originate real estate agreements, so there's
            no rate card to set here.
          </p>
        </Card>
      ) : null}
      {q.data?.brokerage_type === "MORTGAGE" ? (
        <Card padding="md" style={{ marginTop: 16 }}>
          <h2 style={{ fontSize: 14, marginTop: 0 }}>
            Dual capacity disclosure
          </h2>
          <DualCapacityEditor />
        </Card>
      ) : (
        <>
          <Card padding="md" style={{ marginTop: 16 }}>
            <RateCardEditor />
          </Card>
          <Card padding="md" style={{ marginTop: 16 }}>
            <h2 style={{ fontSize: 14, marginTop: 0 }}>
              Dual capacity disclosure
            </h2>
            <DualCapacityEditor />
          </Card>
          <ComplianceSection />
        </>
      )}
    </>
  );
}

const grid: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 14,
  alignItems: "flex-start",
};

const input: React.CSSProperties = {
  padding: "6px 8px",
  font: "inherit",
  fontSize: 13,
  // These carried no border or background at all, so they read as plain text
  // until focused — the one place in this screen the design pass was missing.
  border: "1px solid var(--rp-color-border)",
  borderRadius: "var(--rp-radius-sm)",
  background: "var(--rp-color-surface)",
  color: "var(--rp-color-text)",
};
